import {
  DELETION_FLAG,
  ERROR_CODE,
  PRIVACY_POLICY_URL,
  UniversalError,
  getLoginFormDataDefaultValues,
  getLoginFormDataSchema,
  isFacilityKey,
  isOrganizationKey,
  isUserKey,
} from "@packages/core";
import { useForm } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import React from "react";
import { type Merge } from "type-fest";
import {
  findFacilities,
  findOperator,
  listFacilityStaffs,
  useAuthenticationStore,
  useGetErrorMessage,
  useTranslate,
} from "../../services";
import { css, cx } from "../../styled-system/css";
import {
  ButtonStyle,
  ExternalLink,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PaperStyle,
  TypographyStyle,
} from "../common";
import { PasswordInput } from "../form-fields";
import { Reha30LogoA } from "../images";
import { PageName, showError } from "../utilities";

export type LoginPageProperties = Merge<
  React.ComponentProps<"div">,
  {
    /** ログイン後に遷移するパス */
    from?: string;
    /** ページ名 */
    name?: string;
    /** ログイン後に遷移しない。 */
    stay?: boolean;
  }
>;
export function LoginPage({ className, from, name, stay, ...properties }: LoginPageProperties) {
  const getErrorMessage = useGetErrorMessage();
  const logIn = useAuthenticationStore(({ logIn }) => logIn);
  const logOut = useAuthenticationStore(({ logOut }) => logOut);
  const translate = useTranslate();
  const navigate = useNavigate();

  const { Field, Subscribe, handleSubmit } = useForm({
    defaultState: { canSubmit: false },
    defaultValues: getLoginFormDataDefaultValues(),
    async onSubmit({ value: { password, userInformation } }) {
      try {
        await logOut(); // TODO: もっと良い実装をする。
      } catch (error) {
        console.error("ログアウトに失敗しました。", error);
        showError(error);
        return;
      }

      let username;
      try {
        username = await logIn(
          userInformation.startsWith("0") && /^[0-9]+$/.test(userInformation)
            ? userInformation.replace(/^0/, "+81")
            : userInformation,
          password,
        );
      } catch (error) {
        console.error("ログインに失敗しました。", error);
        showError(error);
        return;
      }

      if (stay) {
        console.debug("ログイン後の遷移を行いません。");
        return;
      }

      if (from) {
        await navigate({ to: from });
      } else if (isFacilityKey(username)) {
        await navigate({ params: { facilityKey: username }, to: "/facilities/$facilityKey" });
      } else if (isOrganizationKey(username)) {
        let facility;
        try {
          const { facilities } = await findFacilities({
            index: "organizationKey-deleted-gsi",
            condition: [["organizationKey", "=", username]],
            limit: 1,
          });
          facility = facilities.at(0);
        } catch (error) {
          console.error("施設の取得に失敗しました。", error);
          showError(error);
          return;
        }

        if (facility) {
          await navigate({
            params: { facilityKey: facility.facilityKey },
            to: "/facilities/$facilityKey",
          });
        } else {
          showError(
            new UniversalError(ERROR_CODE.FACILITY_NOT_FOUND, {
              message: "ログイン可能な施設が存在しません。",
            }),
          );
        }
      } else if (isUserKey(username)) {
        const operator = await findOperator(username);
        const { facilityStaffs } = await listFacilityStaffs({
          deleted: DELETION_FLAG.DEFAULT,
          userKey: username,
        });

        if (operator) {
          await navigate({ to: "/management/system-dashboard" });
        } else if (facilityStaffs.length > 0) {
          await navigate({
            params: { facilityKey: facilityStaffs[0].facilityKey },
            to: "/facilities/$facilityKey",
          });
        } else {
          showError(new UniversalError(ERROR_CODE.UNIMPLEMENTED));
        }
      } else {
        showError(new UniversalError(ERROR_CODE.UNEXPECTED));
        return;
      }
    },
    validators: {
      onChange: getLoginFormDataSchema(),
    },
  });

  return (
    <main
      {...properties}
      className={cx(
        css({
          alignItems: "center",
          backgroundColor: "default-background",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: "100svh",
          paddingBottom: {
            smDown: "max(16px, env(safe-area-inset-bottom))",
            sm: "max(32px, env(safe-area-inset-bottom))",
          },
          paddingLeft: {
            smDown: "max(16px, env(safe-area-inset-left))",
            sm: "max(24px, env(safe-area-inset-left))",
          },
          paddingRight: {
            smDown: "max(16px, env(safe-area-inset-right))",
            sm: "max(24px, env(safe-area-inset-right))",
          },
          paddingTop: {
            smDown: "max(16px, env(safe-area-inset-top))",
            sm: "max(32px, env(safe-area-inset-top))",
          },
        }),
        className,
      )}
    >
      {name ? <PageName>{name}</PageName> : undefined}

      <form
        className={css(PaperStyle.raw({ borderRadius: "8px", padding: "24px", width: "stretch" }), {
          maxWidth: "384px",
        })}
        onSubmit={function (event) {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div className={css({ display: "flex", justifyContent: "center" })}>
          <Reha30LogoA className={css({ height: "32px", width: "auto" })} />
        </div>

        <Field name="userInformation">
          {function (field) {
            return (
              <FormField
                caution={field.state.meta.errors
                  .map(function (error) {
                    return getErrorMessage(error);
                  })
                  .join("")}
                className={css({ marginTop: "24px" })}
                description={translate("%Username.1%")}
                invalid={field.state.meta.errors.length > 0}
                label={translate("%Username.0%")}
              >
                <FormFieldInput
                  className={FormFieldInputStyle({ width: "stretch" })}
                  onBlur={function () {
                    field.handleBlur();
                  }}
                  onChange={function (event) {
                    field.handleChange(event.target.value);
                  }}
                  value={field.state.value}
                />
              </FormField>
            );
          }}
        </Field>
        <Field name="password">
          {function (field) {
            return (
              <FormField
                caution={field.state.meta.errors
                  .map(function (error) {
                    return getErrorMessage(error);
                  })
                  .join("")}
                className={css({ marginTop: "16px" })}
                description={translate("%Password.2%")}
                invalid={field.state.meta.errors.length > 0}
                label={translate("%Password.0%")}
              >
                <PasswordInput
                  className={css({ width: "stretch" })}
                  onBlur={function () {
                    field.handleBlur();
                  }}
                  onChange={function (event) {
                    field.handleChange(event.target.value);
                  }}
                  value={field.state.value}
                />
              </FormField>
            );
          }}
        </Field>

        <div className={css({ alignItems: "center", display: "flex", marginTop: "16px" })}>
          <Subscribe
            selector={function ({ isSubmitting }) {
              return isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <button
                  className={ButtonStyle({
                    appearance: "contained",
                    borderRadius: "4px",
                    color: "primary",
                    flexGrow: 1,
                    padding: "10px 16px",
                  })}
                  disabled={submitting}
                  type="submit"
                >
                  {translate("%Login.0%")}
                </button>
              );
            }}
          </Subscribe>
        </div>

        <Link
          className={css(
            TypographyStyle.raw({
              color: "default",
              fontSize: "14px",
              lineHeight: 1,
              marginTop: "24px",
              textDecoration: "underline",
            }),
            {
              /** TODO: 実装したら表示する。 */
              display: "none",
            },
          )}
          to={/* TODO: 実際のパスに変更する。 */ "."}
        >
          {translate("%Password.1%")}
        </Link>
      </form>

      <div
        className={css({
          display: "flex",
          gap: "20px",
          justifyContent: "center",
          marginTop: "16px",
        })}
      >
        <ExternalLink
          className={css(
            TypographyStyle.raw({
              color: "muted",
              ellipsis: true,
              fontSize: "12px",
              lineHeight: 1,
            }),
            { textDecoration: "underline", cursor: "pointer" },
          )}
          href={PRIVACY_POLICY_URL}
        >
          {translate("%PrivacyPolicy.0%")}
        </ExternalLink>
      </div>
    </main>
  );
}
